/**
 * Editor bridge — DOM-based helpers to scroll to and flash a segment
 * inside the TipTap editor.
 *
 * SegmentHeading.ts renders an invisible `<h2 data-section-id="...">`
 * boundary node. Visible segment content follows that marker in the
 * ProseMirror doc. We scroll the first visible sibling following the
 * boundary into view and briefly highlight it.
 *
 * This is a v1 replacement for the imperative-handle path described in
 * the spec — it avoids threading a ref through multiple panel layers and
 * keeps Review-tab components decoupled from DraftEditor internals.
 */

const HIGHLIGHT_CLASS = 'review-flash-highlight'
const HIGHLIGHT_OVERLAY_CLASS = 'review-flash-overlay'
// Keep the highlight visible long enough to read — a quick 1.5s flash is
// too easy to miss after the jump. 6s gives the eye time to find it.
const HIGHLIGHT_MS = 6000

export interface FlashOptions {
  excerpt?: string | null
}

/**
 * Scroll to the given segment and paint a transient highlight over the
 * excerpt. Uses a floating overlay rather than wrapping the text in a
 * span, because ProseMirror reconciles its DOM and strips any wrapper
 * elements it didn't create itself (spans survive for maybe 100ms).
 *
 * If `excerpt` is provided and found in the segment body, the highlight
 * narrows to the matching text range. Otherwise the section root gets
 * a class-based highlight.
 */
export function flashSection(sectionId: string, opts: FlashOptions = {}): boolean {
  const boundary = findBoundary(sectionId)
  const target = boundary ? findVisibleSegmentRoot(boundary) : findEditorRoot()
  if (!target) return false

  // Scroll the target into view first so range coords are final.
  const excerptRange = opts.excerpt ? findExcerptRange(target, opts.excerpt) : null
  const scrollInto = excerptRange
    ? rangeSpanElement(excerptRange) ?? target
    : target

  if (scrollInto && typeof scrollInto.scrollIntoView === 'function') {
    // Instant scroll — smooth scrolling makes Range.getClientRects() unreliable
    // until the scroll settles, and the overlay needs the final position.
    scrollInto.scrollIntoView({
      behavior: 'auto',
      block: excerptRange ? 'center' : 'start',
    })
  }

  if (excerptRange) {
    // ProseMirror may render more content asynchronously after a scroll —
    // text we targeted can shift by tens of pixels after the initial paint.
    // Re-measure the Range every animation frame while the highlight is
    // visible so the overlay tracks the text. When the range goes out of
    // sync (DOM edits invalidate it), we stop tracking and remove.
    const overlays: HTMLElement[] = []
    let disposed = false
    const started = performance.now()

    const repaint = () => {
      if (disposed) return
      const rects = Array.from(excerptRange.getClientRects()).filter(
        (r) => r.width > 0 && r.height > 0,
      )
      // Ensure we have exactly rects.length overlays, recycling existing ones.
      while (overlays.length < rects.length) overlays.push(makeOverlayEl())
      while (overlays.length > rects.length) overlays.pop()?.remove()
      // position: fixed coords are viewport-relative — Range.getClientRects()
      // already returns viewport-relative rects, so we can plug them straight
      // in without adding scrollX/scrollY.
      rects.forEach((r, i) => {
        const el = overlays[i]!
        el.style.left = `${r.left - 2}px`
        el.style.top = `${r.top - 2}px`
        el.style.width = `${r.width + 4}px`
        el.style.height = `${r.height + 4}px`
      })
      if (performance.now() - started < HIGHLIGHT_MS) {
        requestAnimationFrame(repaint)
      } else {
        disposed = true
        for (const el of overlays) el.remove()
      }
    }
    requestAnimationFrame(repaint)
  } else {
    target.classList.add(HIGHLIGHT_CLASS)
    window.setTimeout(() => target.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_MS)
  }
  return true
}

function makeOverlayEl(): HTMLElement {
  const el = document.createElement('div')
  el.className = HIGHLIGHT_OVERLAY_CLASS
  // position: fixed lifts the overlay out of any nested stacking context in
  // the app shell — the nested scroll containers and stacking contexts in
  // the editor would otherwise paint over a document.body overlay.
  el.style.position = 'fixed'
  el.style.pointerEvents = 'none'
  el.style.zIndex = '2147483647'
  document.body.appendChild(el)
  return el
}

/**
 * Return the common ancestor element of a Range, useful as a scroll anchor.
 */
function rangeSpanElement(range: Range): HTMLElement | null {
  const node = range.commonAncestorContainer
  return node.nodeType === Node.ELEMENT_NODE
    ? (node as HTMLElement)
    : (node.parentElement ?? null)
}

/**
 * See `flashSection` for the tracking loop that creates overlays via
 * `makeOverlayEl` and re-measures the Range each animation frame.
 */

/**
 * Returns true if the given segment is still present in the DOM, OR — on
 * single-section editor pages that don't render boundary markers — if a
 * ProseMirror editor root exists. Used by the outdated-finding check
 * before opening Fix-with-AI.
 */
export function sectionExists(sectionId: string): boolean {
  return findBoundary(sectionId) !== null || findEditorRoot() !== null
}

/**
 * Returns true if the given excerpt can be found in the given section's
 * DOM content. On single-section editor pages without boundary markers,
 * falls back to searching the entire ProseMirror editor.
 */
export function excerptMatches(sectionId: string, excerpt: string): boolean {
  const boundary = findBoundary(sectionId)
  const target = boundary ? findVisibleSegmentRoot(boundary) : findEditorRoot()
  if (!target) return false
  const haystack = normalizeWhitespace(target.textContent ?? '')
  const needle = normalizeWhitespace(excerpt)
  return needle.length === 0 || haystack.includes(needle)
}

// ===================
// Helpers
// ===================

function findBoundary(sectionId: string): HTMLElement | null {
  if (!sectionId) return null
  // Only match elements whose attribute value equals the sectionId exactly.
  // We also filter out citation-badge spans that carry an empty
  // data-section-id with a shared attribute name.
  return document.querySelector<HTMLElement>(
    `[data-section-id="${cssEscape(sectionId)}"]:not([data-section-id=""])`,
  )
}

/**
 * Fallback when there's no boundary marker in the DOM — happens on the
 * single-section SectionEditorPage where the editor only loads one
 * section's content and the SegmentHeading atom isn't rendered. Returns
 * the ProseMirror content root so flashSection can still search for the
 * excerpt and highlight it.
 */
function findEditorRoot(): HTMLElement | null {
  const root = document.querySelector<HTMLElement>('.ProseMirror')
  if (root && isVisible(root)) return root
  const fallback = document.querySelector<HTMLElement>('[contenteditable="true"]')
  return fallback && isVisible(fallback) ? fallback : null
}

function findVisibleSegmentRoot(boundary: HTMLElement): HTMLElement | null {
  // The boundary itself may be display:none. Walk forward siblings until a
  // visible element, bounded by the next segmentHeading (next boundary).
  let next = boundary.nextElementSibling as HTMLElement | null
  while (next) {
    if (next.hasAttribute('data-section-id')) break
    if (isVisible(next)) return next
    next = next.nextElementSibling as HTMLElement | null
  }
  // Fallback: the boundary's parent (e.g., the ProseMirror contenteditable).
  return boundary.parentElement
}

function isVisible(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el)
  return style.display !== 'none' && style.visibility !== 'hidden'
}

function findExcerptRange(root: HTMLElement, excerpt: string): Range | null {
  const target = normalizeWhitespace(excerpt)
  if (target.length === 0) return null
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let offsetSoFar = 0
  let normalizedDoc = ''
  // Build a map: for each character of the normalized string, remember
  // which text node / offset it came from.
  type Entry = { node: Text; localIndex: number }
  const map: Entry[] = []
  let node = walker.nextNode() as Text | null
  while (node) {
    const text = node.textContent ?? ''
    for (let i = 0; i < text.length; i++) {
      const ch = text[i]!
      const isSpace = /\s/.test(ch)
      if (isSpace) {
        if (normalizedDoc.length === 0 || normalizedDoc.endsWith(' ')) {
          // Skip leading/consecutive spaces (we already have a space or are at start)
          continue
        }
        normalizedDoc += ' '
        map.push({ node, localIndex: i })
      } else {
        normalizedDoc += ch.toLowerCase()
        map.push({ node, localIndex: i })
      }
    }
    offsetSoFar += text.length
    node = walker.nextNode() as Text | null
  }

  const startIdx = normalizedDoc.indexOf(target)
  if (startIdx === -1) return null
  const endIdx = startIdx + target.length - 1
  const startMap = map[startIdx]
  const endMap = map[endIdx]
  if (!startMap || !endMap) return null

  const range = document.createRange()
  range.setStart(startMap.node, startMap.localIndex)
  range.setEnd(endMap.node, endMap.localIndex + 1)
  return range
}

function normalizeWhitespace(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toLowerCase()
}

function cssEscape(s: string): string {
  // Chrome / Safari have CSS.escape; fall back for older test envs.
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(s)
  }
  return s.replace(/["\\]/g, '\\$&')
}
